import { collection, addDoc, deleteDoc, doc, serverTimestamp, Timestamp, getDoc, query, where, orderBy, getDocs } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import type { EntityIntakeDefaults } from './entityFormationDefaults';
import { sendEntityFormationSubmissionNotification } from '../email/emailService';
import { logPrivacyEvent } from '../audit/auditLogger';

/**
 * Submission document structure in Firestore
 */
export interface EntityFormationSubmission {
  createdByUid: string;
  createdByEmail: string;
  status: 'submitted';
  version: number;
  data: EntityIntakeDefaults;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  submittedAt: Timestamp | null;
}

/**
 * Submits entity formation intake data to Firestore
 * @param args - Submit parameters including uid, email, and data
 * @returns The document ID of the created submission
 * @throws Error if auth.currentUser.uid does not match the provided uid
 */
export async function submitEntityFormationIntake(args: { uid: string; email: string; data: EntityIntakeDefaults }): Promise<{ submissionId: string }> {
  const { uid, email, data } = args;

  // Security check: ensure the current user matches the provided uid
  if (auth.currentUser?.uid !== uid) {
    throw new Error('Unauthorized: current user does not match provided uid');
  }

  const submissionsRef = collection(db, 'intakeEntityFormationSubmissions');
  
  const docRef = await addDoc(submissionsRef, {
    createdByUid: uid,
    createdByEmail: email,
    status: 'submitted',
    version: 1,
    data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    submittedAt: serverTimestamp(),
  });

  // Get client name for notification
  let clientName = email;
  try {
    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.displayName) {
        clientName = userData.displayName;
      } else if (userData.firstName && userData.lastName) {
        clientName = `${userData.firstName} ${userData.lastName}`;
      }
    }
  } catch (err) {
    console.warn('Could not load user info for email notification:', err);
  }

  // Send notification email (don't wait for it to complete)
  sendEntityFormationSubmissionNotification(
    email,
    clientName,
    data.legal_business_name || 'Not provided',
    docRef.id
  ).catch((err) => {
    console.error('Failed to send entity formation submission notification email:', err);
  });

  // Get user role for audit logging
  let userRole: 'admin' | 'staff' | 'client' = 'client';
  try {
    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      userRole = (userDoc.data().role || 'client') as 'admin' | 'staff' | 'client';
    }
  } catch (err) {
    console.warn('Could not get user role for audit logging:', err);
  }

  // Log entity formation submission (contains sensitive data - SSN/ITIN)
  logPrivacyEvent(
    uid,
    email,
    userRole,
    'entity_formation_submitted',
    'entity_formation_submission',
    docRef.id,
    `Entity formation form submitted: ${data.legal_business_name || 'N/A'}`,
    {
      businessName: data.legal_business_name,
      hasSSN: !!data.ssn_itin,
    }
  ).catch((err) => console.error('Failed to log entity formation submission:', err));

  return { submissionId: docRef.id };
}

/**
 * Deletes an entity formation draft from Firestore
 * @param draftId - The document ID of the draft to delete
 */
export async function deleteEntityFormationDraft(draftId: string): Promise<void> {
  const draftDocRef = doc(db, 'intakeEntityFormationDrafts', draftId);
  await deleteDoc(draftDocRef);
}

/**
 * Loads a specific entity formation submission by ID
 * @param submissionId - The submission document ID
 * @returns The submission with submissionId, data, and timestamps, or null if not found or access denied
 */
export async function loadEntityFormationSubmissionById(submissionId: string): Promise<{ submissionId: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null; submittedAt: Timestamp | null } | null> {
  const submissionDocRef = doc(db, 'intakeEntityFormationSubmissions', submissionId);
  const docSnapshot = await getDoc(submissionDocRef);
  
  if (!docSnapshot.exists()) {
    return null;
  }

  const docData = docSnapshot.data() as EntityFormationSubmission;
  return {
    submissionId: docSnapshot.id,
    data: docData.data,
    createdAt: docData.createdAt,
    updatedAt: docData.updatedAt,
    submittedAt: docData.submittedAt,
  };
}

/**
 * Loads all entity formation submissions for a user
 * @param uid - The user ID
 * @returns Array of submissions with their IDs and metadata
 */
export async function loadAllEntityFormationSubmissions(uid: string): Promise<Array<{ submissionId: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null; submittedAt: Timestamp | null }>> {
  const submissionsRef = collection(db, 'intakeEntityFormationSubmissions');
  const q = query(
    submissionsRef,
    where('createdByUid', '==', uid),
    orderBy('submittedAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  const submissions: Array<{ submissionId: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null; submittedAt: Timestamp | null }> = [];
  
  querySnapshot.forEach((docSnapshot) => {
    const docData = docSnapshot.data() as EntityFormationSubmission;
    submissions.push({
      submissionId: docSnapshot.id,
      data: docData.data,
      createdAt: docData.createdAt,
      updatedAt: docData.updatedAt,
      submittedAt: docData.submittedAt,
    });
  });

  return submissions;
}

/**
 * Loads all entity formation submissions (admin only)
 * @returns Array of all submissions with their IDs, metadata, and user info
 */
export async function loadAllEntityFormationSubmissionsAdmin(): Promise<Array<{ submissionId: string; createdByUid: string; createdByEmail: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null; submittedAt: Timestamp | null }>> {
  const submissionsRef = collection(db, 'intakeEntityFormationSubmissions');
  const q = query(
    submissionsRef,
    orderBy('submittedAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  const submissions: Array<{ submissionId: string; createdByUid: string; createdByEmail: string; data: EntityIntakeDefaults; createdAt: Timestamp | null; updatedAt: Timestamp | null; submittedAt: Timestamp | null }> = [];
  
  querySnapshot.forEach((docSnapshot) => {
    const docData = docSnapshot.data() as EntityFormationSubmission;
    submissions.push({
      submissionId: docSnapshot.id,
      createdByUid: docData.createdByUid,
      createdByEmail: docData.createdByEmail,
      data: docData.data,
      createdAt: docData.createdAt,
      updatedAt: docData.updatedAt,
      submittedAt: docData.submittedAt,
    });
  });

  return submissions;
}

/**
 * Deletes an entity formation submission (admin only)
 * @param submissionId - The submission document ID
 */
export async function deleteEntityFormationSubmission(submissionId: string): Promise<void> {
  const submissionDocRef = doc(db, 'intakeEntityFormationSubmissions', submissionId);
  await deleteDoc(submissionDocRef);
}

